import React, { useState, useCallback, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";

import "./milestone.css";
import "../common/skeleton-effect.css";
import TreeBuilder from "./tree-builder";
import { getMilestoneStatusApi, submitMilestoneAPI, updateMilestoneAPI, getMilestoneEditApi } from "./milestone-api";
import { sanitizeInput } from "../common/security-utils";
import CommonSnackbar from "../common/reusable-commponent";

export const MilestoneCreate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};

  const [milestoneList, setMilestoneList] = useState([]);
  const [removedMilestone, setRemovedMilestone] = useState([]);

  const [milestoneStatusList, setMilestoneStatusList] = useState([]);
  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });

  const [isLoader, setIsLoader] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [defaultValue, setDefaultValue] = useState(false);
  const [productConfirmation, setProductConfirmation] = useState(false);
  const [paymentImages, setPaymentImages] = useState(false);
  const [payment, setPayment] = useState(false);

  const [errorMsg, setErrorMsg] = useState({
    code: "",
    name: "",
    milestoneflow: "",
  });

  const [groupedError, setGroupedError] = useState({});

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, show: false }));
  }, []);

  const triggerSnackbar = useCallback((message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  }, []);

  const getMilestoneStatus = useCallback(async () => {
    try {
      const response = await getMilestoneStatusApi();
      if (response?.data?.success === true) {
        setMilestoneStatusList(response.data.suggestionlist ?? []);
      }
    } catch (err) {
      // Error handled by api-service
    }
  }, []);

  const getMilestoneEdit = useCallback(async (milestoneId) => {
    setIsLoadingData(true);
    try {
      const response = await getMilestoneEditApi(milestoneId);
      if (response?.data?.success === true) {
        const detail = response.data.milestone ?? {};
        setCode(detail.code ?? "");
        setName(detail.name ?? "");
        setDefaultValue(detail.default ?? false);
        setProductConfirmation(detail.product_confirmation ?? false);
        setPaymentImages(detail.payment_images ?? false);
        setPayment(detail.payment ?? false);
        setMilestoneList(detail.milestoneFlows ?? []);
      }
    } catch (err) {
      // Error handled by api-service
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    getMilestoneStatus();
    if (id != null) {
      getMilestoneEdit(id);
    } else {
      setMilestoneList([
        {
          parent_id: 0,
          box_id: 1,
          status: "Trip start",
          buffer_time: "00:00",
          title: "Trip Started",
          all: false,
          stoplevel: false,
          attachment: false,
          comment: false,
          skip: false,
          signature: false,
        },
      ]);
    }
  }, [id, getMilestoneStatus, getMilestoneEdit]);

  const submitForm = useCallback(async () => {
    setIsLoader(true);
    setErrorMsg({ code: "", name: "", milestoneflow: "" });
    setGroupedError({});

    try {
      const body = {
        code: code ?? "",
        name: name ?? "",
        default: defaultValue,
        product_confirmation: productConfirmation,
        payment_images: paymentImages,
        payment: payment,
        milestoneflow: milestoneList ?? [],
        removedMilestone: removedMilestone,
      };

      const response = id == null ? await submitMilestoneAPI(body) : await updateMilestoneAPI(id, body);

      if (response?.data?.success === true) {
        triggerSnackbar(response.data.message || "Success", true);
        setTimeout(() => navigate(-1), 1500);
      } else if (response?.data?.error) {
        const errors = response.data.error;
        const validationTemp = { code: "", name: "", milestoneflow: "" };
        const groupedErrorTemp = {};

        Object.entries(errors).forEach(([key, value]) => {
          const match = key.match(/^milestoneflow\.(\d+)\.(.+)$/);
          if (match) {
            const index = match[1];
            const field = match[2];
            if (!groupedErrorTemp[index]) groupedErrorTemp[index] = {};
            groupedErrorTemp[index][field] = value;
          } else if (key in validationTemp) {
            validationTemp[key] = Array.isArray(value) ? value[0] : value;
          }
        });

        setErrorMsg(validationTemp);
        setGroupedError(groupedErrorTemp);
        triggerSnackbar("Please fix the validation errors", false);
      } else {
        triggerSnackbar(response?.data?.message || "Something went wrong", false);
      }
    } catch (err) {
      triggerSnackbar("An unexpected error occurred", false);
    } finally {
      setIsLoader(false);
    }
  }, [id, code, name, defaultValue, productConfirmation, paymentImages, payment, milestoneList, removedMilestone, navigate, triggerSnackbar]);


  const addChild = useCallback((parentId) => {
    const newId = Math.floor(1000 + Math.random() * 9000);
    const newNode = {
      box_id: newId,
      status: "",
      parent_id: parentId,
      title: "",
      buffer_time: "00:00",
      all: false,
      comment: false,
      skip: false,
      stoplevel: false,
      attachment: false,
      signature: false,
    };
    setMilestoneList((prev) => [...prev, newNode]);
  }, []);

  const removeNode = useCallback((nodeId) => {
    setMilestoneList((prev) => {
      const nodeIndex = prev.findIndex((x) => x.box_id === nodeId);
      if (nodeIndex > -1 && prev[nodeIndex].id) {
        setRemovedMilestone((rem) => [...rem, prev[nodeIndex].id]);
      }

      const toRemove = new Set();
      const mapChildren = {};
      prev.forEach((n) => {
        if (!mapChildren[n.parent_id]) mapChildren[n.parent_id] = [];
        mapChildren[n.parent_id].push(n.box_id);
      });

      const collect = (cur) => {
        toRemove.add(cur);
        (mapChildren[cur] || []).forEach(collect);
      };
      collect(nodeId);

      return prev.filter((n) => !toRemove.has(n.box_id));
    });
  }, []);

  const updateNode = useCallback((nodeId, newValues) => {
    setMilestoneList((prev) => prev.map((item) => (item.box_id === nodeId ? { ...item, ...newValues } : item)));
  }, []);

  const [zoom, setZoom] = useState(1);

  const handleWheel = (e) => {
    if (e.ctrlKey) {
      e.preventDefault();
      setZoom((z) => {
        const newZoom = z + (e.deltaY > 0 ? -0.05 : 0.05);
        return Math.min(Math.max(newZoom, 0.4), 2);
      });
    }
  };

  const isLeaf = useCallback((nodeId) => !milestoneList.some((n) => n.parent_id === nodeId), [milestoneList]);
  const isFirstNode = useCallback(() => milestoneList.length === 1, [milestoneList]);

  return (
    <div className="">
      <div className="px-2 d-flex align-items-center justify-content-between ">
        <div className="d-flex gap-3 my-3">
          <MoveLeft
            className="cursor-pointer"
            onClick={() => {
              navigate(-1);
            }}
          />
          <p className="fw-semibold m-0">{id != null ? "Edit Milestone" : "New Milestone"}</p>
        </div>
        <div className="d-flex gap-3">
          <Button
            className="cursor-pointer bg-white rounded-pill px-4 py-2  btn-border-grey fs-15 fw-500"
            style={{ border: "1px solid #D4DDE7", color: "#212129" }}
            onClick={() => {
              navigate(-1);
            }}
          >
            Cancel
          </Button>
          <Button
            className={`cursor-pointer dark-purple-btn ${isLoader ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
            onClick={() => {
              submitForm();
            }}
            style={{ minWidth: "100px" }}
          >
            {isLoader ? <Spinner animation="border" size="sm" /> : id != null ? "Update" : "Save"}
          </Button>
        </div>
      </div>
      <div className="col-12 px-2">
        {isLoadingData ? (
          <>
            <div className="row mb-2">
              <div className="col-12 pe-0">
                <div className="bg-white rounded-4 d-flex flex-column align-items-start px-3 py-3">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "180px" }} />

                  <div className="row w-100">
                    {/* Code Field Skeleton */}
                    <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                      <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px" }} />
                      <div className="skeleton" style={{ width: "100%", height: "38px" }} />
                    </div>

                    {/* Name Field Skeleton */}
                    <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                      <div className="skeleton skeleton-text-sm mb-2" style={{ width: "60px" }} />
                      <div className="skeleton" style={{ width: "100%", height: "38px" }} />
                    </div>

                    {/* Checklist Skeleton */}
                    <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                      <div className="skeleton skeleton-text-sm mb-2" style={{ width: "70px" }} />
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="d-flex align-items-center mb-2">
                          <div className="skeleton" style={{ width: "18px", height: "18px", borderRadius: "4px" }} />
                          <div className="skeleton skeleton-text-sm ms-2" style={{ width: "150px" }} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="m-2 bg-white rounded-4 p-3">
              <div className="row">
                <div className="col-12">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "80px" }} />
                  <div className="d-flex flex-wrap gap-3 align-items-center">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <React.Fragment key={index}>
                        <div className="skeleton skeleton-card" style={{ width: "280px", height: "140px" }} />
                        {index < 2 && <div className="skeleton" style={{ width: "50px", height: "2px" }} />}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="row mb-2">
              <div className="col-12">
                <div className="bg-white rounded-4 d-flex flex-column align-items-start px-2 py-2">
                  <div className="fs-14 fw-700" style={{ color: "#28303F" }}>
                    MILESTONE DETAILS
                  </div>

                  <div className="row w-100">
                    <div className="mt-2 col-12 col-md-4  d-flex flex-column align-items-start">
                      <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                        Code <span className="error-text">*</span>
                      </div>
                      <div className="w-100">
                        <input type="text" className="form-control rounded-3" placeholder="Enter here..." value={code} onChange={(e) => setCode(e.target.value)} />
                      </div>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#FF4444",
                          marginTop: "6px",
                          marginLeft: "6px",
                        }}
                      >
                        {errorMsg.code}
                      </span>
                    </div>
                    <div className="mt-2 col-12 col-md-4  d-flex flex-column align-items-start">
                      <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                        Name <span className="error-text">*</span>
                      </div>
                      <div className="w-100">
                        <input type="text" className="form-control rounded-3" placeholder="Enter here..." value={name} onChange={(e) => setName(e.target.value)} />
                      </div>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "start",
                          fontSize: "12px",
                          fontWeight: 400,
                          color: "#FF4444",
                          marginTop: "6px",
                          marginLeft: "6px",
                        }}
                      >
                        {errorMsg.name}
                      </span>
                    </div>
                    <div className="mt-2 col-12 col-md-4  d-flex flex-column align-items-start">
                      <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                        Checklist
                      </div>
                      <div className="d-flex align-items-center cursor-pointer mt-2" onClick={() => setDefaultValue(!defaultValue)}>
                        <input
                          type="checkbox"
                          checked={defaultValue}
                          onChange={(e) => setDefaultValue(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <label className="ms-2 cursor-pointer mb-0">Default</label>
                      </div>

                      <div className="d-flex align-items-center cursor-pointer mt-2" onClick={() => setProductConfirmation(!productConfirmation)}>
                        <input
                          type="checkbox"
                          checked={productConfirmation}
                          onChange={(e) => setProductConfirmation(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <label className="ms-2 cursor-pointer mb-0">Product Confirmation</label>
                      </div>

                      <div className="d-flex align-items-center cursor-pointer mt-2" onClick={() => setPaymentImages(!paymentImages)}>
                        <input
                          type="checkbox"
                          checked={paymentImages}
                          onChange={(e) => setPaymentImages(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <label className="ms-2 cursor-pointer mb-0">Payment Images</label>
                      </div>

                      <div className="d-flex align-items-center cursor-pointer mt-2" onClick={() => setPayment(!payment)}>
                        <input
                          type="checkbox"
                          checked={payment}
                          onChange={(e) => setPayment(e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <label className="ms-2 cursor-pointer mb-0">Payment</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-4 p-3">
              <div className="row">
                <div className="col-12 " style={{ overflow: "none" }}>
                  <div className="fs-14 fw-700" style={{ color: "#28303F", textAlign: "left" }}>
                    LEVELS
                  </div>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      fontSize: "12px",
                      fontWeight: 400,
                      color: "#FF4444",
                      marginTop: "6px",
                      marginLeft: "6px",
                    }}
                  >
                    {errorMsg.milestoneflow}
                  </span>
                  <div className="tree-scroll-container">
                    <div
                      className="tree-zoom-content"
                      style={{
                        transform: `scale(${zoom})`,
                        transformOrigin: "0 0",
                      }}
                      onWheel={handleWheel}
                    >
                      <TreeBuilder data={milestoneList} isLeaf={isLeaf} isFirstNode={isFirstNode} addChild={addChild} removeNode={removeNode} updateNode={updateNode} statusList={milestoneStatusList} groupedError={groupedError} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <CommonSnackbar
        show={snackbar.show}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        isSuccess={snackbar.status}
      />
    </div>
  );
};

export function buildTree(list) {
  const map = {};
  const roots = [];
  list.forEach((item) => (map[item.box_id] = { ...item, children: [] }));
  list.forEach((item) => {
    if (item.parent_id === 0 || item.parent_id == null) {
      roots.push(map[item.box_id]);
    } else {
      const parent = map[item.parent_id];
      if (parent) parent.children.push(map[item.box_id]);
      else roots.push(map[item.box_id]); // fallback
    }
  });
  return roots;
}
